import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEvaluateData, type FairRuleResult } from '../hooks/useEvaluateData';
import { useFairDecisions, type CreateDecisionPayload } from '../hooks/useFairDecisions';
import { useApiClient } from '../hooks/useApiClient';
import { EvaluateRuleCard } from '../components/evaluate/EvaluateRuleCard';
import { DecisionDialog, type DecisionAction, type DecisionDialogResult } from '../components/evaluate/DecisionDialog';

type FilterMode = 'all' | 'pending' | 'reviewed' | 'stale' | 'failed';

interface EvaluateContentProps {
  jobId: string | undefined;
  /**
   * Optional rule sub-id to scroll to / expand on mount. The route-bound
   * shell pulls this from the `?rule=` query param; embedded callers can
   * pass it directly (or omit).
   */
  initialRuleId?: string | null;
  /**
   * When true, render without the dark full-viewport PageShell wrapper.
   * Used by the FAIR tab in JourneyNavigator so the body inherits the
   * journey-detail page's light theme. Default false (route-bound use
   * keeps the dark shell).
   */
  embedded?: boolean;
  /**
   * Optional journey group_id. When set, an "Export PDF" button is rendered
   * in the header that hits the FAIR station report endpoint with the
   * current HITL-overlaid rules. Hidden when undefined (route-bound uses
   * don't have a group_id in scope).
   */
  groupId?: string;
}

/**
 * Body of the Evaluate page. Extracted from the route-bound EvaluatePage
 * so the FAIR tab in JourneyNavigator can mount it inline without
 * inheriting the dark full-viewport PageShell.
 */
export function EvaluateContent({ jobId, initialRuleId = null, embedded = false, groupId }: EvaluateContentProps) {
  const { data, loading, error, refetch } = useEvaluateData(jobId);
  const { createDecision } = useFairDecisions();
  const { authFetch } = useApiClient();

  const [filter, setFilter] = useState<FilterMode>('all');
  const [activeRule, setActiveRule] = useState<{ rule: FairRuleResult; action: DecisionAction } | null>(null);
  const [exporting, setExporting] = useState<false | 'pdf' | 'html' | 'xlsx'>(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async (format: 'pdf' | 'html' | 'xlsx') => {
    if (!groupId) return;
    setExporting(format);
    setExportError(null);
    try {
      const response = await authFetch(
        `/api/journey/${encodeURIComponent(groupId)}/report?station=fair_assessment&format=${format}`,
      );
      if (!response.ok) {
        const errBody = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}: ${errBody.slice(0, 240) || 'export failed'}`);
      }
      // Prefer the server-supplied filename from Content-Disposition so the
      // download stem reflects the journey name (v1.1.9+). Fall back to a
      // group-id stem if the header is missing or unparseable.
      const cd = response.headers.get('content-disposition') || '';
      const cdMatch = /filename\*?=(?:UTF-8'')?["']?([^"';\r\n]+)/i.exec(cd);
      const suggestedName = cdMatch?.[1]
        ? decodeURIComponent(cdMatch[1].trim())
        : `FAIR_Assessment_${groupId.substring(0, 8)}.${format}`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = suggestedName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  const filteredRules = useMemo(() => {
    if (!data) return [] as FairRuleResult[];
    return data.rules.filter((r) => {
      const dec = r.evidence_structured.human_decision;
      switch (filter) {
        case 'pending':  return !dec;
        case 'reviewed': return !!dec;
        case 'stale':    return dec?.stale === true;
        case 'failed':   return r.status === 'FAIL';
        case 'all':
        default:         return true;
      }
    });
  }, [data, filter]);

  // Auto-scroll + auto-expand if a rule sub-id was passed.
  useEffect(() => {
    if (!initialRuleId || !data) return;
    const el = document.getElementById(`rule-${initialRuleId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [initialRuleId, data]);

  const Wrapper = embedded ? EmbeddedShell : PageShell;

  if (loading) return <Wrapper><p style={{ color: 'var(--tri-slate)' }}>Loading…</p></Wrapper>;
  if (error) {
    // Old journeys analyzed before v1.1.6 don't have the structured FAIR
    // rule results HITL needs. Detect the specific backend error and surface
    // an actionable message instead of a raw HTTP exception.
    const isPreHitlJourney =
      error.includes('INVALID_STATE') ||
      error.includes('no FAIR rule results') ||
      error.includes('422');
    if (isPreHitlJourney) {
      return (
        <Wrapper>
          <div
            style={{
              padding: 16,
              border: '1px solid var(--tri-soft)',
              background: 'var(--tri-soft-bg)',
              borderRadius: 8,
              color: 'var(--tri-body)',
              fontSize: '0.9rem',
              lineHeight: 1.5,
            }}
          >
            <strong>This journey&rsquo;s FAIR results don&rsquo;t expose human-in-the-loop data.</strong>
            <p style={{ margin: '6px 0 0 0' }}>
              The stored result was written before the persisted shape included structured
              rule data, OR by an analysis path that doesn&rsquo;t produce it. To enable
              Accept / Reject / Edit on this dataset, create a new journey against the same
              source — Databricks-backed runs from the journey-creation form will populate
              the rule data automatically.
            </p>
          </div>
        </Wrapper>
      );
    }
    return <Wrapper><p style={{ color: 'var(--tri-hard)' }}>Failed to load Evaluate data: {error}</p></Wrapper>;
  }
  if (!data) return null;

  const datasetNaturalKey = data.scope_context.dataset_natural_key;
  const schemaNaturalKey  = data.scope_context.schema_natural_key;
  const catalogNaturalKey = data.scope_context.catalog_natural_key;

  const totalRules = data.rules.length;
  const reviewed = data.review_thoroughness.accepted + data.review_thoroughness.rejected + data.review_thoroughness.edited;
  const pending = data.review_thoroughness.pending;

  const handleAction = (rule: FairRuleResult, action: DecisionAction) => {
    setActiveRule({ rule, action });
  };

  const handleSubmit = async (result: DecisionDialogResult) => {
    const payload: CreateDecisionPayload = {
      rule_id: result.rule_id,
      scope_type: result.scope_type,
      scope_natural_key: result.scope_natural_key,
      action: result.action,
      asserted_status: result.asserted_status,
      rationale: result.rationale || null,
      machine_verdict_at_decision_time: result.machine_verdict_at_decision_time,
      asserted_maturity_tier: result.asserted_maturity_tier,
      machine_maturity_tier_at_decision_time: result.machine_maturity_tier_at_decision_time,
    };
    await createDecision(payload);
    setActiveRule(null);
    await refetch();
  };

  // Both the route-bound page and the journey-detail tab sit on the cream
  // ground since the v2.0 fold, so the old dark/light fork collapses.
  const mutedColor = 'var(--tri-slate)';
  const valueColor = 'var(--tri-ink)';

  const exportButtonStyle: React.CSSProperties = {
    padding: '6px 12px',
    background: 'var(--tri-terra)',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    opacity: exporting ? 0.6 : 1,
  };

  return (
    <Wrapper>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', margin: 0 }}>FAIR Evaluate</h1>
          <p style={{ color: mutedColor, margin: '4px 0 0 0', fontSize: '0.85rem' }}>
            Job: <span style={{ color: valueColor, fontFamily: 'JetBrains Mono, Consolas, monospace' }}>{jobId}</span>
            {' · Engine: '}<span style={{ color: valueColor }}>{data.engine_version}</span>
            {' · Tier weights: '}<span style={{ color: valueColor }}>{data.tier_weights.foundational}:{data.tier_weights.core}:{data.tier_weights.maturity}</span>
          </p>
        </div>
        {groupId && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => handleExport('pdf')}
              disabled={exporting !== false}
              style={exportButtonStyle}
              title="Download a PDF of this FAIR assessment, including all human decisions, tier rubrics, and review status."
            >
              {exporting === 'pdf' ? 'Exporting…' : 'Export PDF'}
            </button>
            <button
              type="button"
              onClick={() => handleExport('xlsx')}
              disabled={exporting !== false}
              style={{ ...exportButtonStyle, background: 'var(--tri-teal)' }}
              title="Download a multi-sheet Excel workbook (Summary, Rules, Evidence, Tier Rubric, Recommendations, Decisions). 70 datasets concat into one master sheet via the Rules sheet's stable schema."
            >
              {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
            </button>
            <button
              type="button"
              onClick={() => handleExport('html')}
              disabled={exporting !== false}
              style={{ ...exportButtonStyle, background: 'transparent', color: 'var(--tri-teal)', border: '1px solid var(--tri-taupe)' }}
              title="Download as standalone HTML"
            >
              {exporting === 'html' ? 'Exporting…' : 'HTML'}
            </button>
          </div>
        )}
      </div>
      {exportError && (
        <div style={{ marginBottom: 12, padding: 10, background: 'var(--tri-hard-bg)', border: '1px solid var(--tri-hard-line)', borderRadius: 4, color: 'var(--tri-hard)', fontSize: '0.85rem' }}>
          Export failed: {exportError}
        </div>
      )}

      <ProgressBar reviewed={reviewed} total={totalRules} pending={pending} stale={data.review_thoroughness.stale} />

      <PillarSummary scores={data.pillar_scores} rules={data.rules} />

      <FilterBar value={filter} onChange={setFilter} />

      <div style={{ marginTop: 16 }}>
        {filteredRules.length === 0 ? (
          <p style={{ color: mutedColor, fontSize: '0.85rem' }}>No rules match this filter.</p>
        ) : (
          filteredRules.map((rule) => (
            <EvaluateRuleCard
              key={rule.rule}
              rule={rule}
              currentEngineVersion={data.engine_version}
              defaultExpanded={initialRuleId === rule.rule}
              onAccept={(r) => handleAction(r, 'accept')}
              onReject={(r) => handleAction(r, 'reject')}
              onEdit={(r) => handleAction(r, 'edit')}
            />
          ))
        )}
      </div>

      {activeRule && (
        <DecisionDialog
          rule={activeRule.rule}
          initialAction={activeRule.action}
          datasetNaturalKey={datasetNaturalKey}
          schemaNaturalKey={schemaNaturalKey}
          catalogNaturalKey={catalogNaturalKey}
          onSubmit={handleSubmit}
          onCancel={() => setActiveRule(null)}
        />
      )}
    </Wrapper>
  );
}

/**
 * Route-bound page — pulls jobId + ?rule= from URL, wraps EvaluateContent
 * in the cream PageShell. Mounted at /analysis/:job_id/evaluate.
 */
export function EvaluatePage() {
  const { job_id } = useParams<{ job_id: string }>();
  const [searchParams] = useSearchParams();
  const ruleQuery = searchParams.get('rule');
  return <EvaluateContent jobId={job_id} initialRuleId={ruleQuery} embedded={false} />;
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: 24, maxWidth: 1200, margin: '0 auto',
      color: 'var(--tri-body)', background: 'var(--tri-cream)', minHeight: '100vh',
      fontFamily: 'var(--tri-font)',
    }}>
      {children}
    </div>
  );
}

/**
 * Embedded shell — no full-viewport background, no minHeight, padding only.
 * Inherits the surrounding page theme. Used when EvaluateContent is mounted
 * inside the FAIR tab on JourneyNavigator.
 */
function EmbeddedShell({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '4px 0' }}>{children}</div>;
}

interface ProgressBarProps {
  reviewed: number;
  total: number;
  pending: number;
  stale: number;
}

function ProgressBar({ reviewed, total, pending, stale }: ProgressBarProps) {
  const pct = total === 0 ? 0 : (reviewed / total) * 100;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 12px', background: 'var(--tri-card)',
      borderRadius: 8, marginBottom: 14,
    }}>
      <span style={{ color: 'var(--tri-body)', fontSize: '0.85rem', minWidth: 130 }}>
        {reviewed} of {total} rules reviewed
      </span>
      <div style={{
        flex: 1, height: 6, background: 'var(--tri-track)',
        borderRadius: 9999, overflow: 'hidden',
      }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--tri-good)' }} />
      </div>
      <span style={{ color: 'var(--tri-slate)', fontSize: '0.75rem' }}>
        {pending} pending{stale > 0 && ` · ${stale} stale`}
      </span>
    </div>
  );
}

interface PillarSummaryProps {
  scores: { F: number; A: number; I: number; R: number; overall: number };
  rules: FairRuleResult[];
}

function PillarSummary({ scores, rules }: PillarSummaryProps) {
  // F2 is tier-graded (v1.1.7) — surface the achieved tier under the F pillar
  // so reviewers see immediately whether F2 is dragging the F-pillar score.
  const f2 = rules.find((r) => r.rule === 'F2');
  const f2Breakdown = f2?.evidence_structured.maturity_breakdown;
  const f2HumanTier = f2?.evidence_structured.human_decision?.maturity_decision?.asserted_tier;
  const f2Tier =
    typeof f2HumanTier === 'number' ? f2HumanTier : f2Breakdown?.tier_achieved;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        {(['F', 'A', 'I', 'R'] as const).map((p) => (
          <div key={p} style={{
            flex: 1, padding: '10px 12px', background: 'var(--tri-card)', borderRadius: 8,
          }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--tri-slate)', letterSpacing: '0.05em' }}>{p}</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--tri-ink)' }}>{scores[p].toFixed(1)}</div>
            {p === 'F' && f2Breakdown && typeof f2Tier === 'number' && (
              <div
                style={{ marginTop: 4, fontSize: '0.65rem', color: 'var(--tri-slate)' }}
                title="F2 is tier-graded; see the F2 rule card for the rubric."
              >
                F2: {f2Tier === 0 ? 'below T1' : `T${f2Tier}/${f2Breakdown.tier_max}`}
              </div>
            )}
          </div>
        ))}
        <div
          style={{
            flex: 1, padding: '10px 12px', background: 'var(--tri-good-bg)',
            borderRadius: 8, border: '1px solid var(--tri-good)',
          }}
          title="Tier-weighted overall — mean of pillar scores. The PDF and Excel exports also surface a separate Technical Asset Score on a 0–100 scale."
        >
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--tri-good)', letterSpacing: '0.05em' }}>Tier-weighted</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--tri-good)' }}>
            {scores.overall.toFixed(1)}
            <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--tri-good)', marginLeft: 4 }}>/ 5.0</span>
          </div>
        </div>
      </div>
      {f2Breakdown && (
        <p
          style={{
            margin: '0 0 12px 0',
            fontSize: '0.7rem',
            color: 'var(--tri-slate)',
            fontStyle: 'italic',
          }}
        >
          F2 contributes a fractional verdict (achieved tier ÷ tier max) to the F pillar.
          Other rules remain binary. See methodology guide v3 for details.
        </p>
      )}
    </div>
  );
}

type FilterBarProps = { value: FilterMode; onChange: (v: FilterMode) => void };

function FilterBar({ value, onChange }: FilterBarProps) {
  const opts: Array<{ value: FilterMode; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'stale', label: 'Stale' },
    { value: 'failed', label: 'Failed only' },
  ];
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {opts.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{
            padding: '6px 12px', borderRadius: 6,
            background: value === opt.value ? 'rgba(var(--tri-terra-rgb), 0.12)' : 'transparent',
            color: value === opt.value ? 'var(--tri-terra)' : 'var(--tri-slate)',
            border: '1px solid ' + (value === opt.value ? 'rgba(var(--tri-terra-rgb), 0.3)' : 'transparent'),
            cursor: 'pointer', fontSize: '0.8rem',
            fontWeight: value === opt.value ? 700 : 400,
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
